import React from "react";
import {
  Bar,
  BarChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { Badge } from "../ui/badge";

export interface RegionsLayerItem {
  name: string;
  stateName: string;
  count: number;
}

interface RegionsLayerRankingProps {
  items: RegionsLayerItem[];
  totalInLayer: number;
  t: (key: string) => string;
  locale: string;
}

function toPercent(count: number, total: number) {
  if (total <= 0) return 0;
  return (count / total) * 100;
}

export function RegionsLayerRanking({
  items,
  totalInLayer,
  t,
  locale,
}: RegionsLayerRankingProps) {
  const primaryTextStyle = {
    color: "color-mix(in srgb, var(--foreground) 77%, var(--surface))",
  } as const;
  const secondaryTextStyle = {
    color: "color-mix(in srgb, var(--foreground) 66%, var(--surface))",
  } as const;

  if (!items.length) {
    return (
      <div className="text-sm" style={secondaryTextStyle}>
        {t("statistics.noData")}
      </div>
    );
  }

  const barData = items.map((item) => ({
    region: item.name,
    count: item.count,
  }));

  return (
    <div>
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
      <div className="w-full h-[320px]">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart
            data={barData}
            layout="vertical"
            margin={{ left: 10, right: 22, top: 8, bottom: 8 }}
          >
            <CartesianGrid stroke="var(--border)" strokeDasharray="3 3" />
            <XAxis type="number" />
            <YAxis
              type="category"
              dataKey="region"
              width={150}
              tick={{ fill: "currentColor", fontSize: 12 }}
              axisLine={false}
              tickLine={false}
              interval={0}
            />
            <Tooltip
              cursor={{ fill: "var(--muted)" }}
              formatter={(value: number | undefined) => [
                Number(value ?? 0).toLocaleString(locale),
                t("statistics.websitesLabel"),
              ]}
              contentStyle={{
                background: "var(--card)",
                border: "1px solid var(--border)",
                color: "var(--foreground)",
                borderRadius: 8,
              }}
            />
            <Bar
              dataKey="count"
              fill="var(--category-scale-3)"
              radius={[0, 6, 6, 0]}
            />
          </BarChart>
        </ResponsiveContainer>
      </div>

      <div className="max-h-[320px] overflow-y-auto space-y-2 pr-1">
        {items.map((item) => {
          const share = toPercent(item.count, totalInLayer);
          const shareLabel = `${share.toFixed(1)}%`;
          return (
            <div
              key={`${item.name}:${item.stateName}`}
              className="rounded-lg border p-3"
              style={{
                borderColor: "var(--border)",
              }}
            >
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div
                    className="truncate text-sm font-semibold"
                    style={primaryTextStyle}
                  >
                    {item.name}
                  </div>
                  <div className="text-xs" style={secondaryTextStyle}>
                    {item.stateName}
                  </div>
                </div>
                <Badge variant="secondary">{shareLabel}</Badge>
              </div>
              <div className="mt-2 text-sm" style={secondaryTextStyle}>
                {item.count.toLocaleString(locale)}
              </div>
            </div>
          );
        })}
      </div>
      </div>
    </div>
  );
}
